import { permit2Address } from '@uniswap/permit2-sdk'
import { CurrencyAmount, MaxUint256, Token } from '@uniswap/sdk-core'
import { BigNumber, providers } from 'ethers/lib/ethers'
import { Interface } from 'ethers/lib/utils'
import { ApprovalAction, TokenApprovalInfo } from 'uniswap/src/features/transactions/swap/types/trade'

const APPROVAL_FALLBACK_GAS_LIMIT = BigNumber.from(65_000)
const DEFAULT_GAS_PRICE_WEI = BigNumber.from(10e9)
const GAS_LIMIT_MARGIN_NUMERATOR = BigNumber.from(120)
const GAS_LIMIT_MARGIN_DENOMINATOR = BigNumber.from(100)

const ERC20_INTERFACE = new Interface([
  'function allowance(address owner,address spender) view returns (uint256)',
  'function approve(address spender,uint256 amount) returns (bool)',
])

export type LocalFewV2ApprovalInfo = {
  approvalGasFee: string
  revokeGasFee: string
  tokenApprovalInfo: TokenApprovalInfo
}

async function estimateTransactionFee(
  provider: providers.JsonRpcProvider,
  txRequest: providers.TransactionRequest,
): Promise<string> {
  const [estimatedGasLimit, gasPrice] = await Promise.all([
    provider.estimateGas(txRequest).catch(() => APPROVAL_FALLBACK_GAS_LIMIT),
    provider.getGasPrice().catch(() => DEFAULT_GAS_PRICE_WEI),
  ])
  const gasLimitWithMargin = estimatedGasLimit.mul(GAS_LIMIT_MARGIN_NUMERATOR).div(GAS_LIMIT_MARGIN_DENOMINATOR)
  return gasLimitWithMargin.mul(gasPrice).toString()
}

export async function getLocalFewV2ApprovalInfo({
  amount,
  owner,
  provider,
}: {
  amount: CurrencyAmount<Token>
  owner: string
  provider: providers.JsonRpcProvider
}): Promise<LocalFewV2ApprovalInfo> {
  const token = amount.currency
  const spender = permit2Address(token.chainId)
  const allowanceResult = await provider.call({
    to: token.address,
    data: ERC20_INTERFACE.encodeFunctionData('allowance', [owner, spender]),
  })
  const [allowance] = ERC20_INTERFACE.decodeFunctionResult('allowance', allowanceResult)

  if (BigNumber.from(allowance).gte(amount.quotient.toString())) {
    return {
      approvalGasFee: '0',
      revokeGasFee: '0',
      tokenApprovalInfo: { action: ApprovalAction.None, txRequest: null, cancelTxRequest: null },
    }
  }

  const baseTxRequest = {
    chainId: token.chainId,
    from: owner,
    to: token.address,
    value: '0',
  }
  const approveTxRequest = {
    ...baseTxRequest,
    data: ERC20_INTERFACE.encodeFunctionData('approve', [spender, MaxUint256.toString()]),
  }
  const approvalGasFee = await estimateTransactionFee(provider, approveTxRequest)

  if (BigNumber.from(allowance).isZero()) {
    return {
      approvalGasFee,
      revokeGasFee: '0',
      tokenApprovalInfo: {
        action: ApprovalAction.Permit2Approve,
        txRequest: approveTxRequest,
        cancelTxRequest: null,
      },
    }
  }

  const cancelTxRequest = {
    ...baseTxRequest,
    data: ERC20_INTERFACE.encodeFunctionData('approve', [spender, '0']),
  }
  const revokeGasFee = await estimateTransactionFee(provider, cancelTxRequest)

  return {
    approvalGasFee,
    revokeGasFee,
    tokenApprovalInfo: {
      action: ApprovalAction.RevokeAndPermit2Approve,
      txRequest: approveTxRequest,
      cancelTxRequest,
    },
  }
}
